import {useCallback, useEffect} from 'react';
import isEqual from 'react-fast-compare';
import {useDispatch, useSelector} from 'react-redux';

import {assert, assertAndContinue} from 'Asserts';
import {IS_MASKED_COLUMN_DEF} from 'data-frames/DataFrameConstants';
import {DeprecatedDataFrame, IsMaskedDataRow} from 'data-frames/DataFrameTypes';
import addColumn from 'data-frames/util/legacy/addColumn';
import areColumnValuesUnique from 'data-frames/util/legacy/areColumnValuesUnique';
import areDataFrameColumnsCompatible from 'data-frames/util/legacy/areDataFrameColumnsCompatible';
import filterRows from 'data-frames/util/legacy/filterRows';
import {hasOptionalColumn} from 'data-frames/util/legacy/hasColumn';
import replaceRows from 'data-frames/util/legacy/replaceRows';
import {BlockViewContext} from 'insights/universal-blocks/BlockViewContext';
import {BenchlingState} from 'redux-stores/reduceApp';

import {updateBlockMaskingState} from '../BlockViewStateReduxActions';
import {BlockViewStateSelector} from '../reduceBlockViewStates';

export interface IdDataRow {
  id?: unknown;
}

export interface MaskingBlockViewState<DataRow extends IdDataRow = IdDataRow> {
  inputDataFrame: DeprecatedDataFrame<DataRow>;
  maskedDataFrame: DeprecatedDataFrame<DataRow & IsMaskedDataRow>;
}

interface UseMaskedBlockViewStateInput<DataRow extends IdDataRow> {
  inputDataFrame: DeprecatedDataFrame<DataRow>;
}

type UseMaskedBlockViewStateOutput<
  InputDataRow extends IdDataRow,
  OutputDataRow = InputDataRow & IsMaskedDataRow,
> =
  | {
      maskedDataFrame: DeprecatedDataFrame<OutputDataRow>;
      isMaskingEnabled: true;
      onToggleRowMask: (row: InputDataRow) => void;
    }
  | {
      maskedDataFrame: DeprecatedDataFrame<OutputDataRow>;
      isMaskingEnabled: false;
    };

export function inputToMaskedDataFrame<DataRow extends IdDataRow>(
  inputDataFrame: DeprecatedDataFrame<DataRow>
): DeprecatedDataFrame<DataRow & IsMaskedDataRow> {
  return addColumn(inputDataFrame, IS_MASKED_COLUMN_DEF, (_: DataRow) => false);
}

export function isMaskingStateCompatibleWithDataFrameType<DataRow extends IdDataRow>(
  maskingState: MaskingBlockViewState<IdDataRow>,
  inputDataFrame: DeprecatedDataFrame<DataRow>
): maskingState is MaskingBlockViewState<DataRow> {
  return areDataFrameColumnsCompatible(maskingState.inputDataFrame, inputDataFrame);
}

/**
 * Add an isMasked column to a DataFrame where the row's mask can be toggled with the callback.
 *
 * In order to perform the masking operation, we need an id column where each value is unique. This allows us
 * to know what point was used in the toggle mask callback. When the inputDataFrame does not contain an id
 * column, the masked column will be all false values and the callback will not be returned. If the input data
 * changes the mask will be reset.
 */
export default function useMaskedBlockViewState<DataRow extends IdDataRow>({
  inputDataFrame,
}: UseMaskedBlockViewStateInput<DataRow>): UseMaskedBlockViewStateOutput<DataRow> {
  const isMaskingEnabled = hasOptionalColumn(inputDataFrame, 'id');

  assert(
    !isMaskingEnabled || areColumnValuesUnique(inputDataFrame, 'id'),
    'Expected ids in the id column to be unique'
  );

  const {viewStateId: blockViewStateId} = BlockViewContext.useContext();

  const maskingState = useSelector((state: BenchlingState) =>
    new BlockViewStateSelector(state).getMaskingState(blockViewStateId)
  );

  const dispatch = useDispatch();

  const setMaskingState = useCallback(
    (maskingState: MaskingBlockViewState<DataRow> | null) => {
      dispatch(updateBlockMaskingState(blockViewStateId, maskingState));
    },
    [blockViewStateId, dispatch]
  );

  const previousInputDataFrame = maskingState?.inputDataFrame;

  // Reset the masked state whenever the inputDataFrame changes
  useEffect(() => {
    // TODO(BNCH-30430): Revisit masking logic to make it harder for users to accidentaly delete their masks.
    if (!isEqual(inputDataFrame, previousInputDataFrame)) {
      setMaskingState({
        inputDataFrame,
        maskedDataFrame: inputToMaskedDataFrame(inputDataFrame),
      });
    }
  }, [inputDataFrame, previousInputDataFrame, setMaskingState]);

  // There is a possibility of the data frame changing in the Redux store without this hook intervention. The
  // useEffect will handle that case, but since that is async (i.e. we'll only get the new state next render),
  // we need to return something in the intermediate time. The isMaskingStateCompatibleWithDataFrameType case
  // is handling that case by returning the data frame that will be used by the hook. The case mostly needs to be
  // handled for type safety. We can't assert isMaskingStateCompatibleWithDataFrameType because columns might
  // change naturally when the user enables and disables masking.
  const maskedDataFrame =
    maskingState == null || !isMaskingStateCompatibleWithDataFrameType(maskingState, inputDataFrame)
      ? inputToMaskedDataFrame(inputDataFrame)
      : maskingState.maskedDataFrame;

  if (isMaskingEnabled) {
    return {
      maskedDataFrame,
      isMaskingEnabled: true,
      onToggleRowMask: (dataRow: DataRow) => {
        assertAndContinue(
          filterRows(maskedDataFrame, (row) => row.id === dataRow.id).points.length > 0,
          'Expected toggled dataRow.id to exist in the input dataFrame'
        );
        setMaskingState({
          inputDataFrame,
          maskedDataFrame: replaceRows(
            maskedDataFrame,
            (row) => dataRow.id === row.id,
            (row) => ({...row, isMasked: !row.isMasked})
          ),
        });
      },
    };
  } else {
    return {maskedDataFrame: inputToMaskedDataFrame(inputDataFrame), isMaskingEnabled: false};
  }
}
